import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Grid } from '@mui/material';
import { TextInput } from '@tassomutuo-backoffice/ui';

import AcquisitionPhonePrefixSelect from './AcquisitionPhonePrefixSelect';
import {
  ACQUISITION_EMAIL_PATTERN,
  acquisitionNameRule,
} from './acquisitionCustomer';
import { acquisitionPhoneRule } from './acquisitionValidation';

/** The customer identity every Tasso Quinto acquisition collects. */
export interface AcquisitionCustomerValues {
  firstName: string;
  lastName: string;
  email: string;
  /** ISO2 of the selected country; only its `+NN` prefix is sent. */
  phoneCountry: string;
  phone: string;
}

interface IProps<TValues extends FieldValues> {
  control: Control<TValues>;
  /** Keeps the prefix select addressable when two products are on one page. */
  idPrefix: string;
}

/**
 * The Cliente section of an acquisition form. Prestito and CdQ ask the same
 * five questions, accept the same answers and reject the same ones, so they
 * ask them with one control: a name the operator can read, a valid email, a
 * dial code and a telephone that is not a placeholder.
 */
const AcquisitionCustomerFields = <
  TValues extends FieldValues & AcquisitionCustomerValues
>({
  control,
  idPrefix,
}: IProps<TValues>) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <Controller
        control={control}
        name={'firstName' as Path<TValues>}
        rules={{
          required: 'Inserisci il nome',
          validate: acquisitionNameRule,
        }}
        render={({ field, fieldState }) => (
          <TextInput
            label="Nome"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            {...field}
          />
        )}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <Controller
        control={control}
        name={'lastName' as Path<TValues>}
        rules={{
          required: 'Inserisci il cognome',
          validate: acquisitionNameRule,
        }}
        render={({ field, fieldState }) => (
          <TextInput
            label="Cognome"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            {...field}
          />
        )}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <Controller
        control={control}
        name={'email' as Path<TValues>}
        rules={{
          required: "Inserisci l'email",
          pattern: {
            value: ACQUISITION_EMAIL_PATTERN,
            message: 'Inserisci un indirizzo email valido',
          },
        }}
        render={({ field, fieldState }) => (
          <TextInput
            label="Email"
            type="email"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            {...field}
          />
        )}
      />
    </Grid>
    <Grid item xs={6} sm={3}>
      <Controller
        control={control}
        name={'phoneCountry' as Path<TValues>}
        render={({ field }) => (
          <AcquisitionPhonePrefixSelect
            id={`${idPrefix}-phone-prefix`}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    </Grid>
    <Grid item xs={6} sm={3}>
      <Controller
        control={control}
        name={'phone' as Path<TValues>}
        rules={{
          required: 'Inserisci il telefono',
          validate: acquisitionPhoneRule,
        }}
        render={({ field, fieldState }) => (
          <TextInput
            label="Telefono"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            {...field}
          />
        )}
      />
    </Grid>
  </Grid>
);

export default AcquisitionCustomerFields;
